import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { InListingBannerProps } from '~/components/atoms/InListingBanner/InListingBanner'
import type { MyPortfolioBannerProps } from '~/components/blocks/MyPortfolioBanner/MyPortfolioBanner'
import {
  articleCard,
  ctas,
  detectBodyModuleTypes,
  getBodyModules,
  internationalizedArrayField,
  internationalizedField,
  internationalizedFieldWithFallback,
  myPortfolioBanner,
  pageLanguageFilter,
} from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb } from '~/utils/breadcrumb'
import type { IPaginationContext } from '~/utils/doPagination'
import { getPagination } from '~/utils/doPagination'
import { metaQuery } from '~/utils/seo'
import { resolveMetaWithTemplates } from '~/utils/seoHelper'
import uniqueObjects from '~/utils/uniqueObjects'
import { resolveUrlLinksWithTranslation } from '~/utils/urlResolver'

import { getArtistIndexData } from '../getArtistIndexData'

export interface ArticlesPageData {
  theme?: string
  articles: ArticlePreviewCardProps[]
  breadcrumb: BreadcrumbProps['list']
  featuredArticles: ArticlePreviewCardProps[]
  inListingBanners: InListingBannerProps
  myPortfolioBanner?: MyPortfolioBannerProps
  totalArticles: number
  articleArtists: any
  settings: any
  queryParams: any
  parentQuery: string
  totalPerPage?: number
  pageNumber?: number
  articlesTypeSlug: string | boolean
  articlesType: string | boolean
  otherFilterCats: {
    name: string
    slug: string
    order?: number | null
  }[]
  featuredCategories: {
    name: string
    slug: string
    order?: number | null
    associatedCategories?: { name: string; slug: string }[]
  }[]
  indexSecondaryCategories: {
    name: string
    slug: string
    order?: number | null
  }[]
  otherCategories: {
    secondaryCategories: {
      name: string
      slug: string
      order?: number | null
    }[]
  }[]
  categorySpecificContent?: {
    theme?: 'magazine' | 'light' | 'dark' | null
    body?: Array<{
      _type: string
    }>
    slug: string
    pageBuilderModules2?: Array<{
      _type: string
    }>
    title: string
    summary: PortableTextComponentProps['blocks']
    featuredArticles: ArticlePreviewCardProps[]
    podcastInfo?: {
      listenOnTitle: string
      listenOn: {
        link: string
        logo: any
      }[]
    }
    subCategories: {
      slug: string
      name: string
      summary: any
      leadImage: any
    }[]
    calloutArticles?: {
      title: string
      theme: 'secondary' | 'primary'
      article: ArticlePreviewCardProps
      imageOnLeft?: boolean
    }[]
  }
  isArtistIndex: boolean
  artistIndexContent: {
    name: string
    slug: string
  }
  categoryArtistArticles: any
  lang?: string
}

interface FetchResponse {
  meta: {
    meta: any
    _id: string
  }
  articles: ArticlePreviewCardProps[]
  featuredArticles: ArticlePreviewCardProps[]
  otherCategories: ArticlesPageData['otherCategories']
  categorySpecificContent?: {
    theme?: string
    body?: Array<{
      _type: string
    }>
    meta?: {
      title: string
      description: string
    }
    pageBuilderModules2?: Array<{
      _type: string
    }>
    podcastInfo?: {
      listenOnTitle: string
      listenOn: {
        link: string
        logo: any
      }[]
    }
    title: string
    summary: string
    featuredArticles: any
    subCategories: {
      slug: string
      name: string
      summary: any
      leadImage: any
    }[]
    calloutArticles?: {
      theme: 'secondary' | 'primary'
      title: string
      article: ArticlePreviewCardProps
      imageOnLeft?: boolean
    }[]
  }
  categoryArtistArticles: any
}

interface CategoryParams {
  categoryParam: string
  parentQuery: string
  onlyParentQuery: string
  categoryArtistArticles: string
  articleType: boolean | string
  isArtistIndex: boolean
  isCategoryIndex: boolean
}

interface QueryParams {
  limiter: any
  parentQuery: string
  onlyParentQuery: string
  modulesQuery: string
  artistQuery: string
  categoryArtistArticles: string
}

const buildCategoryParams = (categoryParam?: string): CategoryParams => {
  const defaultParams: CategoryParams = {
    categoryParam: '',
    parentQuery: '',
    onlyParentQuery: '',
    categoryArtistArticles: '',
    articleType: false,
    isArtistIndex: false,
    isCategoryIndex: false,
  }

  if (!categoryParam) return defaultParams

  let params = {
    ...defaultParams,
    categoryParam,
    onlyParentQuery: `&& primaryCategory->slug.current == '${categoryParam.replace('artist-', '')}'`,
  }
  params.isCategoryIndex = true
  params.articleType = 'category'

  if (categoryParam.includes('artist-')) {
    params = handleArtistCategory(params)
  } else if (categoryParam.includes('genre-')) {
    params = handleGenreCategory(params)
  } else {
    params = handleDefaultCategory(params)
  }

  return params
}

const handleArtistCategory = (params: CategoryParams): CategoryParams => {
  const trimString = params.categoryParam.replace('artist-', '')

  return {
    ...params,
    isArtistIndex: true,
    parentQuery: `&& parent->slug.current == '${trimString}'  || secondaryCategories[]->slug.current match '${trimString}'`,
    categoryParam: trimString,
  }
}

const handleGenreCategory = (params: CategoryParams): CategoryParams => {
  const trimString = params.categoryParam.replace('genre-', '')
  return {
    ...params,
    parentQuery: `&& parent->slug.current == '${trimString}'`,
    onlyParentQuery: `&& parent->slug.current == '${trimString}'`,
    categoryParam: trimString,
    isArtistIndex: false,
  }
}

const handleDefaultCategory = (params: CategoryParams): CategoryParams => {
  console.log(`Default category params for ${params.categoryParam}`)
  return {
    ...params,
    parentQuery: `&& (primaryCategory->slug.current == '${params.categoryParam}' || secondaryCategories[]->slug.current match '${params.categoryParam}')`,
    onlyParentQuery: `&& primaryCategory->slug.current == '${params.categoryParam}'`,
    categoryArtistArticles: `"categoryArtistArticles": *[_type == 'article' && secondaryCategories[]->slug.current match '${params.categoryParam}' && defined(parent) && parent->_type == 'artist' && (!defined(language) || language == $lang)]{
      "_id": parent->_id,
      "name": parent->name,
      "image": parent->image,
      "slug": parent->slug.current,
      "isHidden": parent->isHidden,
      "totalArticles": count(*[_type == 'article' && parent._ref == ^.parent._ref && secondaryCategories[]->slug.current match '${params.categoryParam}' && (!defined(language) || language == $lang)])
    },`,
  }
}

const buildModulesQuery = (
  categoryParam?: string,
  lang = 'en',
  detectedTypes?: string[]
): string => {
  if (!categoryParam) return ''

  const hasModules = detectedTypes && detectedTypes.length > 0

  return `"categorySpecificContent": *[_type == 'category' && slug.current == "${categoryParam}"][0] {
    "slug": slug.current,
    ${
      hasModules
        ? `"body": body[]${resolveUrlLinksWithTranslation(lang)} {
      ${getBodyModules(lang, detectedTypes)}
    },`
        : '"body": [],'
    }
    ${metaQuery('category')},
    ${
      hasModules
        ? `"pageBuilderModules2": pageBuilderModules2[]${resolveUrlLinksWithTranslation(lang)} {
      ${getBodyModules(lang, detectedTypes)}
    },`
        : '"pageBuilderModules2": [],'
    }
    // TODO: remove the lang === 'en' branch once the translation service backfills
    // translation.metadata for English-only articles. English articles without a
    // translation.metadata document currently lose all fields from the spread.
    "featuredArticles": featuredArticles[]->{
      ${
        lang === 'en'
          ? `${articleCard},
        "leadImage": coalesce(leadImage, video->coverImage, podcast->coverImage),`
          : `...*[_type == "translation.metadata" && references(^._id)][0].translations[_key == $lang][0].value->{
        ${articleCard},
        "leadImage": coalesce(leadImage, video->coverImage, podcast->coverImage),
      },`
      }
      imageCredit
    },
    "subCategories": subCategories[]->{
      "slug": slug.current,
      "name": ${internationalizedField('name')},
      "summary": ${internationalizedArrayField('summary', lang)},
      leadImage
    },
    "calloutArticles": calloutArticles[]{
      "title": ${internationalizedField('title')},
      "theme": theme,
      "imageOnLeft": imageOnLeft,
      ...*[_type == "translation.metadata" && references(^.article._ref)][0].translations[_key == $lang][0].value->{
        "article":{
          ${articleCard}
        },
        "leadImage": coalesce(leadImage, video->coverImage, podcast->coverImage),
      },
    },
    "podcastInfo": {
      "listenOnTitle": ${internationalizedField('listenOnTitle')},
      "listenOn": listenOn
    },
    "title": ${internationalizedField('name')},
    "summary": ${internationalizedArrayField('summary', lang)},
    theme,
  },`
}

const buildArtistQuery = (
  isArtistIndex: boolean,
  categoryParam?: string
): string => {
  return isArtistIndex
    ? `"artistIndexContent": *[_type == 'artist' && slug.current == "${categoryParam}"][0] {
        name,
        "slug": slug.current
      },`
    : ''
}

const processModules = async (modules: any[] | undefined) => {
  if (!modules) return modules

  return Promise.all(
    modules.map(async (item: any) => {
      if (item._type === 'artworkData') {
        return await getArtistIndexData({ artworkData: item, yearsBack: 5 })
      }
      return item
    })
  )
}

const buildMainQuery = (params: QueryParams, lang = 'en'): string => {
  const {
    limiter,
    parentQuery,
    onlyParentQuery,
    modulesQuery,
    artistQuery,
    categoryArtistArticles,
  } = params

  return `{
    "id": _id,
    "meta": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}][0] { meta, _id },
    "articles": *[_type == 'article' && isVisible != false && (defined(video) || defined(leadImage)) && isFeatured != true && !(_id in path("drafts.**")) && (!defined(language) || language == $lang) ${parentQuery}] | order(coalesce(documentPriority, -1) desc, publishDate desc) [$offset...$limit]{
      ${articleCard}
    },
    ${modulesQuery}
    "featuredCategories": *[_type == 'category' && isFeatured == true && isHidden != true && type != 'press'] | order(categoryOrder asc) {
      "name": ${internationalizedField('name')},
      "slug": slug.current,
      "associatedCategories": associatedCategories[]-> {
        "name": ${internationalizedField('name')},
        "slug": slug.current
      }
    },
    "indexSecondaryCategories": *[_type == 'category' && isFeatured != true && isHidden != true && type == 'article'] {
      "name": ${internationalizedField('name')},
      "slug": slug.current,
      isHidden
    },
    "otherCategories": *[_type == 'article' && (!defined(language) || language == $lang) ${onlyParentQuery} && isHidden != true]{
      secondaryCategories[]-> {
        "name": ${internationalizedFieldWithFallback('name')},
        "slug": slug.current,
        isHidden
      }
    },
    ${categoryArtistArticles}
    "articleArtists": *[_type == 'artist' && count(*[_type == 'article' && parent._ref == (^._id) && (!defined(language) || language == $lang)]) > 0]{
      _id,
      name,
      image,
      "slug": slug.current,
      "totalArticles": count(*[_type == 'article' && parent._ref == (^._id) && (!defined(language) || language == $lang)])
    },
    ${myPortfolioBanner}
    "totalArticles": count(*[_type == 'article' && (defined(video) || defined(leadImage))  && isFeatured != true && !(_id in path("drafts.**")) && (!defined(language) || language == $lang)]),
    "featuredArticles": *[_type == 'article' && isFeatured == true && !(_id in path("drafts.**")) && (defined(video) || defined(leadImage)) && (!defined(language) || language == $lang)] | order(publishDate desc)[] {
      ${articleCard},
      imageCredit
    },
    "settings": *[_type == 'settings' ] [0] {
      "magazineIndexBanner": {
        pretitle,
        title,
        string,
        image,
        ${ctas}
      },
    },
    ${artistQuery}
  }`
}

const handleMetaProps = async (
  metaProps: any,
  data: any,
  seoTemplates: any,
  categoryParam?: string
) => {
  // Use centralized SEO helper with templates passed from page
  await resolveMetaWithTemplates(metaProps, seoTemplates, 'page', categoryParam)

  // Override with category-specific meta if available
  if (categoryParam && data?.categorySpecificContent?.meta?.title) {
    metaProps.meta.title = data.categorySpecificContent.meta.title
  }

  return metaProps
}

export const getArticlesPageProps = async (
  context: IPaginationContext,
  seoTemplates: any,
  categoryParam?: string,
  isArtistIndex = false
) => {
  const { offset, limit, nextPageNumber, totalPerPage, pageNumber } =
    getPagination(context)
  const lang = context?.params?.lang || 'en'
  const categoryParams = buildCategoryParams(categoryParam)
  const breadcrumbPieces = [
    'articles',
    ...(categoryParam ? [categoryParam] : []),
  ]
  const breadcrumb = buildBreadcrumb(
    breadcrumbPieces,
    '',
    undefined,
    undefined,
    lang
  )

  const {
    client,
    limiter,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = await useSanity()

  // Detect body module types first to optimize query size
  let detectedTypes: string[] | undefined
  if (categoryParam) {
    console.log('there is a category param')
    const detectionSelector = `*[_type == 'category' && slug.current == "${categoryParam}"]`

    try {
      detectedTypes = await detectBodyModuleTypes(
        client,
        detectionSelector,
        { lang },
        ['body', 'pageBuilderModules2']
      )
      console.log(`Detected module types for ${categoryParam}:`, detectedTypes)
    } catch (error) {
      console.error('Error detecting body module types:', error)
      // Fall back to no type detection if it fails
      detectedTypes = undefined
    }
  }

  const modulesQuery = buildModulesQuery(categoryParam, lang, detectedTypes)

  const artistQuery = buildArtistQuery(
    categoryParams.isArtistIndex,
    categoryParams.categoryParam
  )

  const mainQuery = buildMainQuery(
    {
      limiter,
      parentQuery: categoryParams.parentQuery,
      onlyParentQuery: categoryParams.onlyParentQuery,
      modulesQuery,
      artistQuery,
      categoryArtistArticles: categoryParams.categoryArtistArticles,
    },
    lang
  )

  const { meta: metaProps, ...data } = await client.fetch<FetchResponse>(
    mainQuery,
    {
      slug: 'articles',
      offset,
      limit,
      lang,
    }
  )

  if (data.categorySpecificContent) {
    // Process modules if they exist
    data.categorySpecificContent.body = await processModules(
      data.categorySpecificContent.body
    )
    data.categorySpecificContent.pageBuilderModules2 = await processModules(
      data.categorySpecificContent.pageBuilderModules2
    )
  }

  const processedMetaProps = await handleMetaProps(
    metaProps,
    data,
    seoTemplates,
    categoryParam
  )

  const otherFilterCats =
    (categoryParams.isArtistIndex || categoryParams.isCategoryIndex) &&
    data.otherCategories.length
      ? uniqueObjects(
          data.otherCategories
            .filter((item: any) => item.secondaryCategories !== null)
            .flatMap((item: any) => item.secondaryCategories),
          'slug'
        )
      : []

  return {
    meta: processedMetaProps.meta,
    notFound: Object.keys(data).length < 1,
    url:
      isArtistIndex || categoryParam?.startsWith('genre-')
        ? `/${categoryParam}/articles`
        : categoryParam
          ? `/articles/${categoryParam}`
          : '/articles',
    isMagazine: true,
    ...data,
    otherFilterCats,
    hasGoogleReviews: true,
    queryParams: {
      slug: 'articles',
      nextPage: nextPageNumber,
    },
    categoryArtistArticles: data.categoryArtistArticles
      ? uniqueObjects(data.categoryArtistArticles, 'slug')
      : null,
    articlesTypeSlug: categoryParams.categoryParam,
    articlesType: categoryParams.articleType,
    smallHeader: true,
    totalPerPage,
    pageNumber,
    breadcrumb,
    noAnimatedTheme: true,
    isArtistIndex: categoryParams.isArtistIndex,
    theme: data?.categorySpecificContent?.theme || 'light',
  }
}
